import { LayoutParams, LayoutResult, TileStyle, SpeakerSide, PipShape, PipPosition, SharedFillRow } from './types';
import {
  BORDER_RADIUS_8,
  NO_RADIUS,
  SHARED_FILL_TILE_AR,
  TARGET_TILE_AR,
  SCREENSHARE_TILE_AR_4_3,
  GRID_DISTRIBUTION,
  SHARED_FILL_DISTRIBUTION
} from './constant';
import { tile, incompleteRow, buildSpeakerGrid, pipRectPosition } from './utils';

// ═══════════════════════════════════════════════════════════════════════════
// Layout computers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * FILL – edge-to-edge, no gaps, no border radius.
 *
 * Row heights are derived from tile width × 16:9 AR so every row is
 * proportionally correct.  The grid is scaled to exactly fill the
 * container height.  Incomplete rows use the max-cols tile width so
 * their tiles are the same size as their neighbours, then centred.
 */
function computeFill(W: number, H: number, count: number, showBorders: boolean, roundedCorners: boolean): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  const dist = GRID_DISTRIBUTION[Math.min(count, 24)] || GRID_DISTRIBUTION[24];
  const { lastIdx, isIncomplete: lastIsIncomplete, tileW: incompleteTileW } = incompleteRow(dist, gap, usableW);

  // Natural (AR-preserving) row height = tileWidth / 16:9
  const naturalRowH = dist.map((cols, i) => {
    const tw = i === lastIdx && lastIsIncomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    return tw / TARGET_TILE_AR;
  });

  const rowCount = dist.length;
  const vGaps = (rowCount - 1) * gap;
  // Scale so all rows together fill exactly usableH
  const scale = (usableH - vGaps) / naturalRowH.reduce((a, b) => a + b, 0);

  const tiles: TileStyle[] = [];
  let topOffset = margin;

  for (let r = 0; r < dist.length; r++) {
    const cols = dist[r];
    const incomplete = r === lastIdx && lastIsIncomplete;
    const cellW = incomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    const cellH = naturalRowH[r] * scale;
    const rowW = cols * cellW + (cols - 1) * gap;
    const leftStart = incomplete ? margin + (usableW - rowW) / 2 : margin;

    for (let c = 0; c < cols; c++) {
      tiles.push(tile(topOffset, leftStart + c * (cellW + gap), cellW, cellH, W, H, radius));
    }
    topOffset += cellH + gap;
  }

  return tiles;
}

/**
 * TILES – all speakers with gaps and rounded corners.
 *
 * Each row's height is derived from its tile width and TARGET_TILE_AR so
 * every tile shares the same display aspect ratio.  When the AR-based grid
 * is taller than the container the heights are scaled down proportionally
 * (tiles get slightly flatter but stay uniform).  When shorter, the grid
 * is centred vertically.
 */
function computeTiles(W: number, H: number, count: number, showBorders: boolean, roundedCorners: boolean): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;

  const distTable = GRID_DISTRIBUTION;
  const dist = distTable[Math.min(count, 24)] || distTable[24];
  // TILES always maintains 16:9 aspect ratio (scale capped at 1)
  const scaleCap = 1;

  return buildSpeakerGrid(
    dist,
    gap,
    W - 2 * margin, // usableW
    H - 2 * margin, // containerH
    margin, // baseY
    margin, // left margin
    W,
    H,
    radius,
    scaleCap
  );
}

/**
 * SHARED_FILL – split view with screen share on one side, speakers on the other.
 *
 * Speaker panel takes ~32% of width, screen takes ~66%.
 * Gap between screen and speakers panel = 1.5%.
 *
 * Each speaker tile's width is derived from the column count of its row,
 * and tile height = width / TARGET_TILE_AR (matching 16:9 webcam feeds).
 * When tiles don't fit vertically they are scaled down uniformly.
 */
function computeSharedFill(
  W: number,
  H: number,
  count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  side: SpeakerSide
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;
  const panelGap = showBorders ? W * 0.01 : 0;
  const tileGap = showBorders ? W * 0.01 : 0;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // For 1 speakers use a narrower panel so the screen share dominates
  const panelRatio = count < 2 ? 0.24 : 0.32;
  const speakerPanelW = usableW * panelRatio;
  const screenW = usableW - speakerPanelW - panelGap;

  const isLeft = side === 'LEFT';
  const speakerLeft = isLeft ? margin : margin + screenW + panelGap;
  const screenLeft = isLeft ? margin + speakerPanelW + panelGap : margin;

  const tiles: TileStyle[] = [];

  const screenH = usableH * 0.75;
  const screenTop = margin + (usableH - screenH) / 2;
  tiles.push(tile(screenTop, screenLeft, screenW, screenH, W, H, radius));

  // --- Speaker tiles ---
  const dist = SHARED_FILL_DISTRIBUTION[Math.min(count, 24)] || SHARED_FILL_DISTRIBUTION[24];
  const rowCount = dist.length;
  const vGaps = (rowCount - 1) * tileGap;

  /** Width of a single tile in a row with `cols` columns. */
  const colWidth = (cols: number): number => (speakerPanelW - (cols - 1) * tileGap) / cols;

  /** Tile width for a row. fullWidth rows span the panel.
   *  Remainder rows (last row with fewer cols) inherit the previous row's tile width
   *  so leftover speakers stay the same size as the rest of the grid. */
  const tileWidthForRow = (row: SharedFillRow, idx: number): number => {
    if (row.fullWidth) {
      return speakerPanelW;
    }
    // Last row with fewer cols than the preceding row — inherit preceding width
    if (idx > 0 && idx === rowCount - 1) {
      const prev = dist[idx - 1];
      if (!prev.fullWidth && row.cols < prev.cols) {
        return colWidth(prev.cols);
      }
    }
    return colWidth(row.cols);
  };

  // Compute each row's height using its per-row scaleRatio.
  const availableH = usableH - vGaps;
  const rowHeights = dist.map((row, i) => {
    const naturalH = tileWidthForRow(row, i) / SHARED_FILL_TILE_AR;
    return naturalH * (row.scaleRatio ?? 1.7);
  });

  // If total exceeds available space, scale everything down uniformly to fit.
  const totalDesired = rowHeights.reduce((a, b) => a + b, 0);
  const fitScale = totalDesired > availableH ? availableH / totalDesired : 1;
  const finalRowHeights = rowHeights.map(h => h * fitScale);

  // Center the grid vertically
  const totalGridH = finalRowHeights.reduce((a, b) => a + b, 0) + vGaps;
  let topOffset = margin + Math.max(0, (usableH - totalGridH) / 2);

  for (let r = 0; r < rowCount; r++) {
    const row = dist[r];
    const cols = row.cols;
    const tw = tileWidthForRow(row, r);
    const th = finalRowHeights[r];

    if (row.fullWidth) {
      tiles.push(tile(topOffset, speakerLeft, tw, th, W, H, radius));
    } else if (cols < (dist[Math.min(r + 1, rowCount - 1)]?.cols ?? cols)) {
      // Row with fewer tiles than the next — centre in the panel
      const rowW = cols * tw + (cols - 1) * tileGap;
      const rowLeft = speakerLeft + (speakerPanelW - rowW) / 2;
      for (let c = 0; c < cols; c++) {
        tiles.push(tile(topOffset, rowLeft + c * (tw + tileGap), tw, th, W, H, radius));
      }
    } else if (r === rowCount - 1 && r > 0 && cols < dist[r - 1].cols) {
      // Remainder row — centre the tiles in the panel
      const rowW = cols * tw + (cols - 1) * tileGap;
      const rowLeft = speakerLeft + (speakerPanelW - rowW) / 2;
      for (let c = 0; c < cols; c++) {
        tiles.push(tile(topOffset, rowLeft + c * (tw + tileGap), tw, th, W, H, radius));
      }
    } else {
      for (let c = 0; c < cols; c++) {
        tiles.push(tile(topOffset, speakerLeft + c * (tw + tileGap), tw, th, W, H, radius));
      }
    }

    topOffset += th + tileGap;
  }

  return tiles;
}

/**
 * FILM_STRIP – large screen share with a speaker strip.
 *
 * Always single column up to 12 speakers. Tile height is derived by dividing
 * usableH equally across all speakers including gaps — no overflow, no scroll.
 *
 * Tile aspect ratio is 4:3 (landscape) so faces are wider and more visible
 * even at high speaker counts where height is constrained. Strip width =
 * tile width and is capped at 22 % of usableW so the screen share always
 * dominates.
 *
 * All tile coordinates use cqw / cqh so they scale to any container size.
 */
function computeFilmStrip(
  W: number,
  H: number,
  count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  side: SpeakerSide,
  keepAspectRatio: boolean
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;
  const stripGap = showBorders ? W * 0.01 : 0;
  const tileGap = showBorders ? W * Math.min(0.048 / count, 0.012) : 0;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // Single column always. Distribute usableH equally — no overflow, no scroll.
  const vGaps = Math.max(count - 1, 0) * tileGap;
  const maxTileH = (usableH - vGaps) / count;

  // keepAspectRatio: 16:9 to match webcam feeds; otherwise 4:3 landscape (33% wider than square).
  const tileAr = keepAspectRatio ? TARGET_TILE_AR : SCREENSHARE_TILE_AR_4_3;

  // Cap at 22% of usableW so screen share always keeps at least ~76% of the width.
  const tileW = Math.min(maxTileH * tileAr, usableW * 0.22);

  // Enforce aspect ratio: if tileW was capped, shrink tileH to match.
  const tileH = Math.min(maxTileH, tileW / tileAr);
  const stripW = tileW;
  const screenW = usableW - stripW - stripGap;

  // Center the strip vertically when tiles don't fill the full height
  const totalStripH = count * tileH + vGaps;
  const stripTopOffset = margin + (usableH - totalStripH) / 2;

  const isLeft = side === 'LEFT';
  const stripLeftPx = isLeft ? margin : margin + screenW + stripGap;
  const screenLeft = isLeft ? margin + stripW + stripGap : margin;

  const tiles: TileStyle[] = [];

  // Tile 0: Screen share – fills full usable height
  tiles.push(tile(margin, screenLeft, screenW, usableH, W, H, radius));

  // Speaker tiles: single column, vertically centered, respecting 4:3 aspect ratio
  for (let i = 0; i < count; i++) {
    const topPx = stripTopOffset + i * (tileH + tileGap);
    tiles.push(tile(topPx, stripLeftPx, tileW, tileH, W, H, radius));
  }

  return tiles;
}

/**
 * PIP – full-screen screen share with a small speaker overlay.
 * The PiP position is controlled separately via PIP_POSITION_STYLES.
 */
function computePip(
  W: number,
  H: number,
  _count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  pipShape: PipShape,
  pipPosition: PipPosition = 'RIGHT_BOTTOM',
  keepAspectRatio: boolean = false
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  const tiles: TileStyle[] = [];

  // Element 0: Screen share (full container)
  tiles.push(tile(margin, margin, usableW, usableH, W, H, radius));

  // Element 1: PiP speaker overlay
  if (pipShape === 'CIRCULAR') {
    // Circular: 14% width, square aspect ratio, border-radius 100%.
    // Uses explicit top/left (via pipRectPosition + tile()) rather than
    // auto/bottom/right/transform so the editor's selection hit-box
    // (which only reads top/left/width/height) can always find it.
    const pipW = usableW * 0.14;
    const pipH = pipW;
    const { pipTop, pipLeft } = pipRectPosition(pipPosition, pipW, pipH, margin, margin, usableW, usableH, W, H);
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, '100%', { zIndex: 5 }));
  } else {
    // Rectangular: 18% width, anchored to the selected corner. keepAspectRatio
    // uses 16:9 to match webcam feeds; otherwise use 4:3.
    const pipAr = keepAspectRatio ? TARGET_TILE_AR : SCREENSHARE_TILE_AR_4_3;
    const pipW = usableW * 0.18;
    const pipH = pipW / pipAr;
    const { pipTop, pipLeft } = pipRectPosition(pipPosition, pipW, pipH, margin, margin, usableW, usableH, W, H);
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, radius, { zIndex: 5 }));
  }

  return tiles;
}

/**
 * ONLY_SCREEN – screen share takes the entire stage.
 */
function computeOnlyScreen(
  W: number,
  H: number,
  _count: number,
  showBorders: boolean,
  roundedCorners: boolean
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  return [tile(margin, margin, usableW, usableH, W, H, radius)];
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════

export function computeLandscapeLayout(params: LayoutParams): LayoutResult {
  const {
    layout,
    aspectRatio,
    speakerCount,
    showBorders,
    roundedCorners,
    speakerSide = 'LEFT',
    pipShape = 'RECTANGULAR',
    pipPosition = 'RIGHT_BOTTOM' as const,
    keepAspectRatio = false
  } = params;

  if (aspectRatio <= 0) {
    return { tiles: [] };
  }

  // Normalised coordinate space: W is always 100, H scales with aspect ratio.
  // tile() emits cqw/cqh so the browser resolves them against the real container.
  const W = 100;
  const H = aspectRatio * 100;
  const count = Math.max(1, Math.min(speakerCount, 24));

  switch (layout) {
    case 'FILL':
      return { tiles: computeFill(W, H, count, showBorders, roundedCorners) };

    case 'TILES':
      return { tiles: computeTiles(W, H, count, showBorders, roundedCorners) };

    case 'SHARED_FILL':
      return { tiles: computeSharedFill(W, H, count, showBorders, roundedCorners, speakerSide) };

    case 'FILM_STRIP':
      return { tiles: computeFilmStrip(W, H, count, showBorders, roundedCorners, speakerSide, keepAspectRatio) };

    case 'PIP':
      return { tiles: computePip(W, H, count, showBorders, roundedCorners, pipShape, pipPosition, keepAspectRatio) };

    case 'ONLY_SCREEN':
      return { tiles: computeOnlyScreen(W, H, count, showBorders, roundedCorners) };

    default:
      return { tiles: [] };
  }
}
